/**
 * iOS haptic target integration.
 *
 * `ios-haptics` works only when the user physically taps the native switch it
 * overlays on a control. A detached switch activated with `.click()` is not a
 * trusted iOS touch, so consumers receive a ref callback for their actual
 * touch target instead of an imperative `trigger()` function.
 */

import { useCallback, useRef, type RefCallback } from 'react';
import { attachHapticTarget, type HapticPattern } from '@/lib/haptics';

/**
 * Decorates a real, already-interactive target with the native iOS switch.
 *
 * The injected switch receives the physical tap and lets the event bubble to
 * the target's existing handler. It is removed from the accessibility tree so
 * it does not appear as an extra unnamed checkbox to assistive technology.
 */
export function useHapticTarget<T extends HTMLElement>(pattern?: HapticPattern): RefCallback<T> {
  const attachedTargets = useRef(new WeakSet<T>());

  return useCallback(
    (node: T | null) => {
      if (!node || attachedTargets.current.has(node)) return;
      attachHapticTarget(node, pattern);
      attachedTargets.current.add(node);
    },
    [pattern]
  );
}
